import axios from "axios";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;
const baseUrlOrigin = (() => {
  try {
    return baseUrl ? new URL(baseUrl).origin : null;
  } catch {
    return null;
  }
})();
const AUTH_HEADER = "Authorization";
const NGROK_SKIP_HEADER = "ngrok-skip-browser-warning";
const NGROK_SKIP_VALUE = "skip";
const BOARD_HEADERS = ["board"];
const GRADE_HEADERS = ["grade"];
const USER_CONTEXT_HEADER_STORAGE_KEY = "userContextHeaders";
const ONBOARDING_STORAGE_KEY = "onboardingInfo";

const userContextHeaderValues = {
  board: null,
  grade: null,
};

const canUseBrowserStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const sanitizeUserContextValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();
  return normalized.length ? normalized : null;
};

const readUserContextHeadersFromStorage = () => {
  if (!canUseBrowserStorage()) {
    return null;
  }

  const storageKeys = [USER_CONTEXT_HEADER_STORAGE_KEY, ONBOARDING_STORAGE_KEY];

  for (const key of storageKeys) {
    try {
      const rawValue = window.localStorage.getItem(key);
      if (!rawValue) {
        continue;
      }

      const parsed = JSON.parse(rawValue);
      const board = sanitizeUserContextValue(parsed?.board);
      const grade = sanitizeUserContextValue(parsed?.grade);

      if (board || grade) {
        return { board, grade };
      }
    } catch (error) {
      // Ignore malformed storage entries
    }
  }

  return null;
};

const persistUserContextHeaders = () => {
  if (!canUseBrowserStorage()) {
    return;
  }

  if (!userContextHeaderValues.board && !userContextHeaderValues.grade) {
    window.localStorage.removeItem(USER_CONTEXT_HEADER_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(
    USER_CONTEXT_HEADER_STORAGE_KEY,
    JSON.stringify(userContextHeaderValues)
  );
};

const hydrateUserContextHeaders = () => {
  const stored = readUserContextHeadersFromStorage();
  if (!stored) {
    return;
  }

  userContextHeaderValues.board = stored.board;
  userContextHeaderValues.grade = stored.grade;
};

hydrateUserContextHeaders();

export const setUserContextHeaders = ({ board, grade } = {}) => {
  userContextHeaderValues.board = sanitizeUserContextValue(board);
  userContextHeaderValues.grade = sanitizeUserContextValue(grade);
  persistUserContextHeaders();
};

export const clearUserContextHeaders = () => {
  userContextHeaderValues.board = null;
  userContextHeaderValues.grade = null;
  persistUserContextHeaders();
};

const getStoredToken = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return (
    window.localStorage?.getItem("token") ??
    window.sessionStorage?.getItem("token") ??
    null
  );
};

const hasAuthHeader = (headers = {}) => {
  if (!headers) {
    return false;
  }

  if (typeof headers.has === "function") {
    return headers.has(AUTH_HEADER) || headers.has(AUTH_HEADER.toLowerCase());
  }

  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    return headers.has(AUTH_HEADER);
  }

  return Object.keys(headers).some(
    (key) => key.toLowerCase() === AUTH_HEADER.toLowerCase()
  );
};

const setHeaderValue = (headers, key, value) => {
  if (typeof headers?.set === "function") {
    headers.set(key, value);
    return headers;
  }

  return {
    ...(headers ?? {}),
    [key]: value,
  };
};

const removeHeaderValue = (headers, key) => {
  if (!headers) {
    return headers;
  }

  const normalizedKey = key.toLowerCase();

  if (typeof headers.delete === "function") {
    headers.delete(key);
    if (normalizedKey !== key) {
      headers.delete(normalizedKey);
    }
    return headers;
  }

  if (typeof headers !== "object") {
    return headers;
  }

  const hasKey =
    Object.prototype.hasOwnProperty.call(headers, key) ||
    (normalizedKey !== key &&
      Object.prototype.hasOwnProperty.call(headers, normalizedKey));

  if (!hasKey) {
    return headers;
  }

  const nextHeaders = { ...headers };
  delete nextHeaders[key];

  if (normalizedKey !== key) {
    delete nextHeaders[normalizedKey];
  }

  return nextHeaders;
};

const applyHeaderList = (headers, headerNames, value) => {
  return headerNames.reduce((acc, headerName) => {
    if (value) {
      return setHeaderValue(acc, headerName, value);
    }
    return removeHeaderValue(acc, headerName);
  }, headers);
};

const applyUserContextHeaders = (headers) => {
  let updatedHeaders = headers;
  const { board, grade } = userContextHeaderValues;

  updatedHeaders = applyHeaderList(updatedHeaders, BOARD_HEADERS, board);
  updatedHeaders = applyHeaderList(updatedHeaders, GRADE_HEADERS, grade);

  return updatedHeaders;
};

if (baseUrl) {
  axios.defaults.baseURL = baseUrl;
}

autosetupAxiosAuthorization();
autosetupFetchAuthorization();

const shouldSkipAuthForUrl = (url) => {
  if (!url) {
    return false;
  }

  if (typeof window === "undefined") {
    return false;
  }

  if (url.includes("/pdfs/") || url.includes(".pdf")) {
    return true;
  }

  try {
    const resolved = new URL(url, window.location.origin);
    const origin = resolved.origin;
    if (
      origin !== window.location.origin &&
      (!baseUrlOrigin || origin !== baseUrlOrigin)
    ) {
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

function autosetupAxiosAuthorization() {
  axios.interceptors.request.use(
    (config) => {
      config.headers = setHeaderValue(
        config.headers,
        NGROK_SKIP_HEADER,
        NGROK_SKIP_VALUE
      );
      config.headers = applyUserContextHeaders(config.headers);

      // Skip authorization for PDF paths
      const url = config.url || "";
      if (shouldSkipAuthForUrl(url)) {
        return config;
      }

      const token = getStoredToken();
      if (!token) {
        return config;
      }

      if (!hasAuthHeader(config.headers)) {
        config.headers = setHeaderValue(
          config.headers,
          AUTH_HEADER,
          `Bearer ${token}`
        );
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
}

function autosetupFetchAuthorization() {
  if (
    typeof window === "undefined" ||
    typeof window.fetch !== "function" ||
    window.__authFetchPatched
  ) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    // Skip authorization for PDF paths
    const url = typeof input === "string" ? input : input.url;
    if (shouldSkipAuthForUrl(url)) {
      return originalFetch(input, init);
    }

    const token = getStoredToken();

    const authValue = token ? `Bearer ${token}` : null;

    const applyHeaders = (headersSource) => {
      const headers = new Headers(headersSource);
      headers.set(NGROK_SKIP_HEADER, NGROK_SKIP_VALUE);
      applyUserContextHeaders(headers);

      if (authValue && !headers.has(AUTH_HEADER)) {
        headers.set(AUTH_HEADER, authValue);
      }

      return headers;
    };

    if (input instanceof Request) {
      const headers = applyHeaders(init.headers ?? input.headers);
      const request = new Request(input, {
        ...init,
        headers,
      });
      return originalFetch(request);
    }

    const headers = applyHeaders(init.headers);
    const updatedInit = {
      ...init,
      headers,
    };

    return originalFetch(input, updatedInit);
  };

  window.__authFetchPatched = true;
}

export default axios;
